import { useMemo } from 'react'
import { useTheme } from 'styled-components'
import { DeleteOutline, EditOutlined } from '@material-ui/icons'
import { useSelector } from 'react-redux'

import {
	Input,
	Select,
	Calendar,
	Checkbox,
	Card as CardV2,
	OtherChargesCard,
	Table,
	Toggle,
	TableActionsWrapper,
	TableActionButton,
} from '@baseV2'
import { enquiryPlatform, REGION_TYPES, SpoStatus, strings } from '@constants'
import { displayAmount, formatMixedQuantities, getActiveCommodity, orderInUnitLabel } from '@utils'
import { isSuperAdmin } from 'src/utils/adminFilter'

import PaymentTermsCard from './PaymentTermsCard'
import {
	Card,
	SectionTitle,
	SectionHeaderRow,
	FieldRow,
	CheckRow,
	SummaryBar,
	SummaryItem,
	HeaderButtons,
	HeaderButton,
	BillingGrid,
	ItemGrid,
	CategoryCard,
	CategoryName,
	AddBadge,
	ItemsTableWrap,
} from './styles'

const platformList = enquiryPlatform?.map((item) => ({ key: item.name, label: item.name })) ?? []
const regionList = Object.entries(REGION_TYPES).map(([key, val]) => ({ key, label: val }))
// ATTACHED is set by the system, so it is not offered in the dropdown — same as the old SPO form.
const statusList = Object.entries(SpoStatus)
	.map(([key, val]) => ({ key, label: val }))
	.filter((item) => item.key !== SpoStatus.ATTACHED)

const ItemsPage = ({
	state,
	edit,
	valueChangeHandler,
	userChangeHandler,
	addNewItemButtonClick,
	addChargeButtonClick,
	editItemButtonClick,
	itemDeleteHandler,
	statusChangeHandler,
}) => {
	const theme = useTheme()
	const { newSpo, users, categories } = state
	const loggedInUser = useSelector((_state) => _state.getIn(['user', 'user']))

	const orderItems = useMemo(() => (newSpo?.order_items ? [...newSpo.order_items] : []), [newSpo])
	const categoryData = categories?.data ?? []
	// FOR / EX quote type (default FOR); EX shows the "X" toggle option active.
	const isQuoteTypeX = newSpo?.quote_type === 'EX'

	// Total Amount (FOR) = Σ allotted_weight × price_per_kg across all added items.
	const totalAmount = useMemo(
		() => orderItems.reduce((acc, item) => acc + (item?.allotted_weight ?? 0) * (item?.price_per_kg ?? 0), 0),
		[orderItems]
	)

	// Total quantity across all added items, grouped by unit (weight items are stored in kg → MT).
	const totalQuantity = useMemo(() => formatMixedQuantities(orderItems, 'allotted_weight'), [orderItems])

	// Added-items table columns — same labels as the Supplier order item view.
	const itemColumns = useMemo(
		() => [
			{ label: 'Category', key: (row) => row?.category_name ?? '--', sortable: false },
			{ label: 'Product', key: (row) => row?.product_name ?? '--', sortable: false },
			{ label: 'Quantity', key: (row) => formatMixedQuantities([row], 'allotted_weight'), sortable: false },
			{ label: 'Pending Quantity', key: (row) => formatMixedQuantities([row], 'pending_weight'), sortable: false },
			{ label: 'Brand', key: (row) => row?.brand_ordered ?? '--', sortable: false },
			{ label: 'Grade', key: (row) => row?.grade ?? '--', sortable: false },
			{ label: 'Rate', key: (row) => displayAmount(row?.price_per_kg), align: 'right', sortable: false },
			{ label: 'TC Required?', key: (row) => (row?.tc_available ? 'Yes' : 'No'), align: 'center', sortable: false },
			{ label: 'HSN Code', key: (row) => row?.hsn_code ?? '--', sortable: false },
			{
				label: 'Actions',
				align: 'right',
				sortable: false,
				renderer: (_, row) => (
					<TableActionsWrapper>
						{/* An SPO must keep at least one item, so the last one cannot be deleted. */}
						<TableActionButton
							color={theme.colors.status.error.text}
							onClick={itemDeleteHandler?.(row)}
							disabled={orderItems?.length <= 1}
							title={orderItems?.length <= 1 ? strings('msg_atleast_one_item_required') : strings('delete')}
						>
							<DeleteOutline />
						</TableActionButton>
						<TableActionButton
							color={theme.colors.brand.primary}
							onClick={(e) => {
								e.stopPropagation()
								editItemButtonClick(row)
							}}
						>
							<EditOutlined />
						</TableActionButton>
					</TableActionsWrapper>
				),
			},
		],
		[theme, itemDeleteHandler, editItemButtonClick, orderItems?.length]
	)

	return (
		<>
			{/* Supplier + basic order fields */}
			<Card>
				<FieldRow>
					<Select
						label={strings('select', 'supplier')}
						placeholder={strings('placeholder_select_supplier')}
						data={users?.data ?? []}
						displayKey='display_name'
						primaryKey='organisation_id'
						value={state?.selectedUser ?? {}}
						onChange={(item) => userChangeHandler(item)}
						disabled={edit}
					/>
					<Input
						label={strings(['supplier', 'po', 'number'])}
						placeholder={strings(['enter', 'supplier', 'po', 'number'])}
						value={newSpo?.original_supplier_po_number ?? ''}
						onChange={(e) => valueChangeHandler('original_supplier_po_number')(e.target.value)}
					/>
					<Calendar
						label={strings('po', 'date')}
						value={newSpo?.supplier_po_date ?? ''}
						onChange={(v) => valueChangeHandler('supplier_po_date')(v)}
					/>
					<Select
						label={strings('platform')}
						placeholder={strings('platform')}
						data={platformList}
						displayKey='label'
						primaryKey='key'
						value={platformList.find((item) => item.key === newSpo?.platform) ?? {}}
						onChange={(item) => valueChangeHandler('platform')(item?.key)}
					/>
					<Select
						label={strings('region')}
						placeholder={strings('region')}
						data={regionList}
						displayKey='label'
						primaryKey='key'
						value={regionList.find((item) => item.key === newSpo?.region) ?? {}}
						onChange={(item) => valueChangeHandler('region')(item?.key)}
					/>
					{/* Status is only changeable on an existing SPO, and only by a super admin. */}
					{edit && (
						<Select
							label={strings('status')}
							placeholder={strings('status')}
							data={statusList}
							displayKey='label'
							primaryKey='key'
							value={statusList.find((item) => item.key === newSpo?.supplier_po_status) ?? {}}
							onChange={(item) => statusChangeHandler?.(item)}
							disabled={!isSuperAdmin(loggedInUser?.role)}
						/>
					)}
				</FieldRow>
			</Card>

			{/* Start adding items */}
			<Card>
				<SectionHeaderRow>
					<SectionTitle>{'Add Items'}</SectionTitle>
					<HeaderButtons>
						<SummaryBar>
							<SummaryItem>
								{strings('items')}: <strong>{orderItems.length}</strong>
							</SummaryItem>
							<SummaryItem>
								Quantity: <strong>{totalQuantity}</strong>
							</SummaryItem>
							<SummaryItem>
								{strings('total_amount_for')}: <strong>{displayAmount(totalAmount)}</strong>
							</SummaryItem>
						</SummaryBar>
						<CheckRow style={{ marginLeft: 'auto' }}>
							<Checkbox
								checked={!!newSpo?.order_in_kgs}
								onChange={(e) => valueChangeHandler('order_in_kgs')(e?.target ? e.target.checked : e)}
							/>
							{orderInUnitLabel(getActiveCommodity(loggedInUser))}
						</CheckRow>
						{/* FOR / EX quote type — common Toggle component (sets newSpo.quote_type). */}
						<Toggle
							options={[
								{ label: strings('for'), active: !isQuoteTypeX, click: () => valueChangeHandler('quote_type')('FOR') },
								{ label: strings('x'), active: isQuoteTypeX, click: () => valueChangeHandler('quote_type')('EX') },
							]}
						/>
						{/* <HeaderButton color='primary' variant='outline' size='small' onClick={addChargeButtonClick}>
							{strings('add', 'charge') || 'Add Charge'}
						</HeaderButton> */}
						<HeaderButton color='primary' variant='solid' size='small' onClick={addNewItemButtonClick}>
							{strings('add', 'item')}
						</HeaderButton>
					</HeaderButtons>
				</SectionHeaderRow>

				{/* Commodity/category cards — clicking any opens the Add Item drawer. */}
				{categoryData.length > 0 && (
					<ItemGrid>
						{categoryData.map((cat, index) => (
							<CategoryCard key={cat?.category_id ?? cat?.category_name ?? index} type='button' onClick={addNewItemButtonClick}>
								<AddBadge>+</AddBadge>
								<CategoryName>{cat?.category_name ?? cat?.name ?? '--'}</CategoryName>
							</CategoryCard>
						))}
					</ItemGrid>
				)}

				{/* Added items — enquiry-style table; click a row to edit. The table renders even with no
				    items so the column headers and the empty state are visible, like the cards below. */}
				<ItemsTableWrap>
					<Table
						columns={itemColumns}
						data={orderItems}
						onRowClick={editItemButtonClick}
						hideFooter
						emptyState={{ text: strings(['No', 'items', 'added']) || 'No items added yet' }}
					/>
				</ItemsTableWrap>
			</Card>

			{/* Other Charges | Payment Terms — half-half, same common cards as the order/enquiry flow. */}
			<BillingGrid>
				<CardV2 hoverable={false}>
					<OtherChargesCard
						charges={newSpo?.other_charges ?? []}
						onSave={(list) => valueChangeHandler('other_charges')(list)}
					/>
				</CardV2>
				<PaymentTermsCard state={state} valueChangeHandler={valueChangeHandler} />
			</BillingGrid>
		</>
	)
}

export default ItemsPage
